/**
 * Google Maps JavaScript API loader
 * Lazy-loads Maps and Places libraries only when needed
 */

declare global {
  interface Window {
    google: typeof google;
  }
}

interface GoogleMapsLoaderOptions {
  language?: string; // e.g., 'en', 'lv'
  apiKey?: string;
}

class GoogleMapsLoader {
  private loadPromise: Promise<void> | null = null;
  private isLoaded = false;

  /**
   * Load Google Maps JavaScript API and Places library
   * Returns a promise that resolves when libraries are ready
   */
  async load(options: GoogleMapsLoaderOptions = {}): Promise<void> {
    console.log('[GoogleMapsLoader] ========== load() called ==========');
    console.log('[GoogleMapsLoader] Options:', {
      hasLanguage: !!options.language,
      language: options.language,
      hasApiKeyOption: !!options.apiKey,
      isAlreadyLoading: !!this.loadPromise,
      isAlreadyLoaded: this.isLoaded,
      hasWindowGoogle: !!window.google,
      hasWindowGoogleMaps: !!window.google?.maps,
    });

    // Return cached promise if already loading
    if (this.loadPromise) {
      console.log('[GoogleMapsLoader] ⏭️ Already loading, returning existing promise');
      return this.loadPromise;
    }

    // Return immediately if already loaded
    if (this.isLoaded && window.google?.maps) {
      console.log('[GoogleMapsLoader] ✅ Already loaded, returning immediately');
      return Promise.resolve();
    }

    // Start loading
    console.log('[GoogleMapsLoader] 🚀 Starting new load process...');
    this.loadPromise = this.loadScripts(options);
    await this.loadPromise;
    this.isLoaded = true;
    console.log('[GoogleMapsLoader] ✅ Load completed successfully');
  }

  private loadScripts(options: GoogleMapsLoaderOptions): Promise<void> {
    return new Promise((resolve, reject) => {
      console.log('[GoogleMapsLoader] ========== loadScripts() called ==========');
      
      // Check environment variable status
      const envApiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
      console.log('[GoogleMapsLoader] Environment check:', {
        hasEnvVar: !!envApiKey,
        envVarType: typeof envApiKey,
        envVarLength: envApiKey ? envApiKey.length : 0,
        hasOptionApiKey: !!options.apiKey,
        mode: import.meta.env.MODE,
        isDev: import.meta.env.DEV,
        isProd: import.meta.env.PROD,
      });

      const apiKey = options.apiKey || envApiKey;

      if (!apiKey) {
        const errorMsg = 'Google Maps API key not found. Set VITE_GOOGLE_MAPS_API_KEY environment variable.';
        console.error('[GoogleMapsLoader] ❌ API KEY MISSING:', {
          error: errorMsg,
          checkedOptionsApiKey: !!options.apiKey,
          checkedEnvVar: !!envApiKey,
          envVarName: 'VITE_GOOGLE_MAPS_API_KEY',
          mode: import.meta.env.MODE,
        });
        reject(new Error(errorMsg));
        return;
      }

      console.log('[GoogleMapsLoader] ✅ API key found:', {
        source: options.apiKey ? 'options parameter' : 'environment variable',
        keyLength: apiKey.length,
        keyPrefix: apiKey.substring(0, 4) + '***',
        keySuffix: '***' + apiKey.substring(apiKey.length - 4),
      });

      // Check if script already exists
      const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
      console.log('[GoogleMapsLoader] Checking for existing script:', {
        foundExistingScript: !!existingScript,
        existingScriptSrc: existingScript ? (existingScript as HTMLScriptElement).src.substring(0, 80) + '...' : null,
      });

      if (existingScript) {
        console.log('[GoogleMapsLoader] ⏭️ Script already exists in DOM, waiting for initialization...');
        // Wait for google.maps to be available
        let attempts = 0;
        const maxAttempts = 50; // 5 seconds total (50 * 100ms)
        const checkInterval = setInterval(() => {
          attempts++;
          if (window.google?.maps) {
            console.log('[GoogleMapsLoader] ✅ Google Maps initialized after', attempts, 'attempts');
            clearInterval(checkInterval);
            resolve();
          } else if (attempts >= maxAttempts) {
            clearInterval(checkInterval);
            const errorMsg = 'Google Maps failed to load - window.google.maps is undefined after timeout';
            console.error('[GoogleMapsLoader] ❌ TIMEOUT:', {
              error: errorMsg,
              attempts,
              hasWindow: typeof window !== 'undefined',
              hasGoogle: typeof window.google !== 'undefined',
              googleValue: window.google,
            });
            reject(new Error(errorMsg));
          }
        }, 100);
        return;
      }

      console.log('[GoogleMapsLoader] No existing script found, creating new script tag...');

      // Build script URL with language parameter
      const language = options.language || 'en';
      const script = document.createElement('script');
      const scriptUrl = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places,geometry&language=${language}&loading=async`;
      script.src = scriptUrl;
      script.async = true;
      script.defer = true;

      console.log('[GoogleMapsLoader] Script configuration:', {
        url: scriptUrl.replace(apiKey, 'API_KEY_MASKED'),
        async: script.async,
        defer: script.defer,
        language,
        libraries: 'places,geometry',
      });

      script.onload = () => {
        console.log('[GoogleMapsLoader] 📥 Script onload event fired');
        console.log('[GoogleMapsLoader] Waiting for google.maps to initialize...');
        // Wait for google.maps to be available
        let attempts = 0;
        const maxAttempts = 50; // 5 seconds total (50 * 100ms)
        const checkInterval = setInterval(() => {
          attempts++;
          if (window.google?.maps) {
            console.log('[GoogleMapsLoader] ✅ Google Maps initialized after', attempts, 'attempts');
            console.log('[GoogleMapsLoader] Available APIs:', {
              hasMaps: !!window.google.maps,
              hasPlaces: !!window.google.maps.places,
              hasGeometry: !!window.google.maps.geometry,
              hasMapConstructor: !!window.google.maps.Map,
            });
            clearInterval(checkInterval);
            resolve();
          } else if (attempts >= maxAttempts) {
            clearInterval(checkInterval);
            const errorMsg = 'Google Maps failed to initialize - window.google.maps is undefined after script load';
            console.error('[GoogleMapsLoader] ❌ INITIALIZATION TIMEOUT:', {
              error: errorMsg,
              attempts,
              hasWindow: typeof window !== 'undefined',
              hasGoogle: typeof window.google !== 'undefined',
              googleType: typeof window.google,
              googleValue: window.google,
              scriptLoaded: true,
            });
            reject(new Error(errorMsg));
          }
        }, 100);
      };

      script.onerror = (error) => {
        const errorMsg = 'Failed to load Google Maps script - check network, CSP, and API key';
        console.error('[GoogleMapsLoader] ❌ SCRIPT LOAD ERROR:', {
          error: errorMsg,
          errorEvent: error,
          scriptSrc: scriptUrl.replace(apiKey, 'API_KEY_MASKED'),
          possibleCauses: [
            'Network error - check browser Network tab',
            'CSP blocking - check Content-Security-Policy header',
            'Invalid API key - check Google Cloud Console',
            'API not enabled - check Google Cloud Console APIs',
            'Referrer restriction - check API key HTTP referrers',
          ],
        });
        reject(new Error(errorMsg));
      };

      console.log('[GoogleMapsLoader] Adding script to DOM...');
      console.log('[GoogleMapsLoader] DOM state before append:', {
        hasDocument: typeof document !== 'undefined',
        hasDocumentHead: !!document.head,
        headChildCount: document.head?.childElementCount || 0,
      });

      document.head.appendChild(script);
      console.log('[GoogleMapsLoader] ✅ Script tag added to DOM');
      console.log('[GoogleMapsLoader] Script URL (masked):', scriptUrl.replace(apiKey, 'API_KEY_MASKED'));
      
      // Verify script was added
      const addedScript = document.querySelector('script[src*="maps.googleapis.com"]');
      if (!addedScript) {
        console.error('[GoogleMapsLoader] ❌ VERIFICATION FAILED: Script tag not found in DOM after append!', {
          searchedFor: 'script[src*="maps.googleapis.com"]',
          allScripts: Array.from(document.querySelectorAll('script')).map(s => ({
            src: (s as HTMLScriptElement).src?.substring(0, 50) || 'no src',
            async: (s as HTMLScriptElement).async,
            defer: (s as HTMLScriptElement).defer,
          })),
        });
      } else {
        console.log('[GoogleMapsLoader] ✅ Verification passed: Script found in DOM');
      }
    });
  }

  /**
   * Check if Google Maps is already loaded
   */
  isReady(): boolean {
    return this.isLoaded && !!window.google?.maps;
  }

  /**
   * Reset loader state (useful for testing)
   */
  reset(): void {
    this.loadPromise = null;
    this.isLoaded = false;
  }
}

// Export singleton instance
export const googleMapsLoader = new GoogleMapsLoader();

/**
 * Convenience function to load Google Maps
 */
export async function loadGoogleMaps(options?: GoogleMapsLoaderOptions): Promise<void> {
  return googleMapsLoader.load(options);
}

/**
 * Check if Google Maps is ready
 */
export function isGoogleMapsReady(): boolean {
  return googleMapsLoader.isReady();
}

